/**
 * useStuckDetection — gentle "stuck" nudge for first-time customers.
 *
 * If the customer is idle for `idleMs` milliseconds (no mouse / keyboard
 * / touch / scroll input) AND no tour bubble has been opened yet on
 * this session, Maya softly offers to walk them through.
 *
 * Why so cautious?
 *   • The nudge MUST never interrupt a thinking customer. We listen
 *     to activity and reset the timer on every interaction.
 *   • We never re-show in the same session. Once dismissed (or once
 *     a tour has been opened) the nudge stays quiet for good.
 *   • Respects prefers-reduced-motion via the StuckNudge component.
 *
 * Returns:
 *   { shouldShow, dismiss }
 *
 * Usage:
 *   const { shouldShow, dismiss } = useStuckDetection({
 *     idleMs: 45_000,
 *     enabled: someCondition,
 *   });
 */
import { useEffect, useRef, useState } from "react";

import useTour from "./useTour";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll", "wheel"];
const SESSION_DISMISSED_KEY = "jns_tour_stuck_dismissed";

export default function useStuckDetection({ idleMs = 45_000, enabled = true } = {}) {
    const [shouldShow, setShouldShow] = useState(false);
    const timerRef = useRef(null);
    const openedRef = useRef(false);
    const { openBeaconId } = useTour();

    // Track whether ANY tour bubble has been opened in this session —
    // if so, the customer doesn't need our nudge anymore.
    useEffect(() => {
        if (openBeaconId) openedRef.current = true;
    }, [openBeaconId]);

    function dismiss() {
        setShouldShow(false);
        try {
            if (typeof window !== "undefined") {
                window.sessionStorage.setItem(SESSION_DISMISSED_KEY, "1");
            }
        } catch { /* ignore */ }
    }

    useEffect(() => {
        if (!enabled || typeof window === "undefined") return undefined;
        // Already dismissed this session? Stay quiet.
        try {
            if (window.sessionStorage.getItem(SESSION_DISMISSED_KEY) === "1") {
                return undefined;
            }
        } catch { /* ignore */ }

        const reset = () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            // If the customer has already opened the tour OR has just
            // dismissed the nudge, don't re-arm.
            if (openedRef.current) return;
            timerRef.current = setTimeout(() => {
                if (!openedRef.current) setShouldShow(true);
            }, idleMs);
        };
        reset();
        ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, reset));
        };
    }, [enabled, idleMs]);

    // Also hide the nudge the moment a beacon opens (the customer
    // self-served and doesn't need us interrupting).
    useEffect(() => {
        if (openBeaconId && shouldShow) setShouldShow(false);
    }, [openBeaconId, shouldShow]);

    return { shouldShow, dismiss };
}
